import type { TenantSummary } from '@rd/shared';
import { TriangleAlert } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * D-05's instrument (ARCHITECTURE §2).
 *
 * A suspended tenant's estate still *renders* — the rows are there, the counts
 * are there — but every write is refused. Without this banner a platform user
 * scoped into that account sees a live-looking panel whose buttons fail one at
 * a time, and concludes the panel is broken rather than that the account is
 * suspended. Naming the account matters too: a platform user has several, and
 * "this account" without a name is a guess.
 *
 * It is a full-bleed band directly under the context bar rather than a line of
 * text in the canvas: this is a statement about the whole screen below it, and
 * a sentence set like body copy is one an operator reads past. The tone is
 * `warning`, which §11.6 refuses to let a tenant brand — a suspension has to
 * look the same in every account.
 */
export function SuspendedTenantBanner({ tenant }: { tenant: TenantSummary | null }): ReactElement | null {
  if (!tenant || tenant.status === 'ACTIVE') return null;

  const isArchived = tenant.status === 'ARCHIVED';

  return (
    <div
      role="status"
      className="flex items-start gap-[var(--space-2)] border-b border-warning/20 bg-warning-soft px-[var(--space-6)] py-[var(--space-2)] text-[length:var(--text-sm)] text-warning-text"
    >
      {/* The glyph is redundant with the colour on purpose: colour alone is not
          a channel, and this band has to survive a monochrome print-out of a
          support screenshot. `aria-hidden` because the sentence says it. */}
      <TriangleAlert aria-hidden="true" size={14} strokeWidth={2} className="mt-[3px] shrink-0" />
      <p>
        <strong className="font-semibold">{tenant.name}</strong>{' '}
        {isArchived
          ? 'is archived. It is visible for reference only and nothing can be changed.'
          : 'is suspended. You can look at everything here, but nothing can be changed until it is reactivated.'}
      </p>
    </div>
  );
}
